import React, { useState } from 'react'
// import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import Modal from './Modal'
import Typography from '../Typography'
import Button from '../Button/Button'
import Select from '../Select/Select'
import InputTextArea from '../Input/InputTextArea'
import useContactOrganizer from '../../hooks/useContactOrganizer'
import CheckIcon from '../Icons/CheckIcon'
import WarningOutlineIcon from '../Icons/WarningOutlineIcon'
import { theme } from '../../theme'

const ContactOrganizerModal = ({ isOpen, onClose, eventId, organizerName }) => {
  const { t } = useTranslation('eventDetail')
  const [message, setMessage] = useState('')
  const [topic, setTopic] = useState('')
  const [success, setSuccess] = useState(false)
  const [validationError, setValidationError] = useState('')

  const { contactOrganizer, loading, error } = useContactOrganizer(eventId)

  const topics = [
    { value: 'general', label: t('contactOrganizerModal.topics.general') },
    { value: 'logistics', label: t('contactOrganizerModal.topics.logistics') },
    { value: 'accessibility', label: t('contactOrganizerModal.topics.accessibility') },
    { value: 'schedule', label: t('contactOrganizerModal.topics.schedule') },
    { value: 'requirements', label: t('contactOrganizerModal.topics.requirements') },
    { value: 'other', label: t('contactOrganizerModal.topics.other') }
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    setValidationError('')

    if (!message.trim() && !topic) {
      setValidationError(t('contactOrganizerModal.validationErrors.topicAndMessage'))
      return
    }

    if (!topic) {
      setValidationError(t('contactOrganizerModal.validationErrors.topic'))
      return
    }

    if (!message.trim()) {
      setValidationError(t('contactOrganizerModal.validationErrors.message'))
      return
    }

    const result = await contactOrganizer({
      message: message.trim(),
      topic: topic
    })

    if (result.success) {
      setSuccess(true)
      setMessage('')
      setTopic('')
      setTimeout(() => {
        setSuccess(false)
        onClose()
      }, 2000)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setMessage('')
      setTopic('')
      setSuccess(false)
      setValidationError('')
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <Modal title={t('contactOrganizerModal.title')} onClose={handleClose} maxWidth="500px">
      <ModalContent>
        {success ? (
          <SuccessContainer>
            <SuccessIcon>
              <CheckIcon fill={theme.colors.successMain} />
            </SuccessIcon>
            <Typography $variant="title-5-semibold" as="h3">
              {t('contactOrganizerModal.messageSent.title')}
            </Typography>
            <Typography $variant="body-1-regular" color={theme.colors.defaultWeak}>
              {t('contactOrganizerModal.messageSent.description')}
            </Typography>
          </SuccessContainer>
        ) : (
          <FormContainer onSubmit={handleSubmit}>
            <FormWrapper>

              <FormDescription>
                <Typography $variant="body-1-regular" color={theme.colors.defaultStrong} as="p">
                  {t('contactOrganizerModal.description', { organizerName })}
                </Typography>
              </FormDescription>

              {(error || validationError) && (
                <ErrorContainer>
                  <WarningOutlineIcon width="20" height="20" fill={theme.colors.errorMain} />
                  <Typography $variant="body-2-medium" color={theme.colors.errorMain}>
                    {error || validationError}
                  </Typography>
                </ErrorContainer>
              )}

              <FormField>
                {/* <Label> */}
                <Typography $variant="body-2-medium" as="label">
                  {t('contactOrganizerModal.topicLabel')}
                </Typography>
                {/* </Label> */}
                <Select
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  disabled={loading}
                  size="l"
                >
                  <option value="">{t('contactOrganizerModal.topicPlaceholder')}</option>
                  {topics.map((topicOption) => (
                    <option key={topicOption.value} value={topicOption.value}>
                      {topicOption.label}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField>
                {/* <Label> */}
                <Typography $variant="body-2-medium" as="label">
                  {t('contactOrganizerModal.messageLabel')}
                </Typography>
                {/* </Label> */}
                <InputTextArea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('contactOrganizerModal.messagePlaceholder')}
                  rows={5}
                  disabled={loading}
                  maxLength={1000}
                  $size="large"
                />
                <CharacterCount>
                  <Typography $variant="body-3-regular" color={theme.colors.defaultWeak}>
                    {message.length}/1000
                  </Typography>
                </CharacterCount>
              </FormField>
            </FormWrapper>

            <ButtonContainer>
              <Button
                type="button"
                onClick={handleClose}
                disabled={loading}
                $variant="outline"
                size="l"
                sizeTablet="l"
                sizeMobile="m"
                style={{ flex: 1 }}
              >
                {t('contactOrganizerModal.cancelButton')}
              </Button>
              <Button
                type="submit"
                disabled={loading}
                $variant=""
                size="l"
                sizeTablet="l"
                sizeMobile="m"
                style={{ flex: 1 }}
              >
                {loading ? t('contactOrganizerModal.sendingButton') : t('contactOrganizerModal.sendButton')}
              </Button>
            </ButtonContainer>
          </FormContainer>
        )}
      </ModalContent>
    </Modal>
  )
}

export default ContactOrganizerModal

// Styled Components
const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  // padding: ${({ theme }) => theme.sizing.sm};
  // gap: ${({ theme }) => theme.sizing.sm};
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${({ theme }) => theme.sizing.lg} 0;
  gap: ${({ theme }) => theme.sizing.xs};
`

const SuccessIcon = styled.div`
  width: ${({ theme }) => theme.sizing.xl};
  height: ${({ theme }) => theme.sizing.xl};
  background-color: ${({ theme }) => theme.fill.successAlphaMain16};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  // margin-bottom: ${({ theme }) => theme.sizing.xs};
`

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  // gap: ${({ theme }) => theme.sizing.sm};
`

const FormDescription = styled.div`
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
`;


const CharacterCount = styled.div`
  text-align: right;
`;

const ErrorContainer = styled.div`
  padding: ${({ theme }) => theme.sizing.sm};
  background-color: ${({ theme }) => theme.fill.elserrorAlphaMain16};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.sizing.xs};
  border-radius: ${({ theme }) => theme.radius.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.xs};
  }
`

const ButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.xs};
  border-top: 1px solid ${({ theme }) => theme.border.defaultWeak};
  padding: ${({ theme }) => theme.sizing.sm};
`

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.sizing.sm};
  gap: ${({ theme }) => theme.sizing.sm};
  width: 100%;
  max-width: 100%;
`;